import React, { useState, useEffect } from 'react';
import { API_URL } from "../url_config";
import {
  Button,
  Table,
  Input,
  Space,
  message,
  Card,
  Tag,
  Drawer,
  Form,
  Typography,
} from 'antd';
import {
  ReloadOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { NavigationStepsCapNoc } from "../components/navbars/navbar_cap_noc";

const { Title } = Typography;

export default function PierreCapNoc() {
  const apiUrl = `${API_URL}/validations/pb_cnx/?ot_tag=cap_noc`;

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Token ${token}` } : {};
  };

  const [data, setData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingField, setEditingField] = useState({ id: null, field: null });
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [newRow, setNewRow] = useState({
    email: '',
    poste: '',
    applications: '',
    comportement: '',
    prise_en_compte: false,
    traitee_verifiee: false,
    commentaire: '',
    ot_tag: 'cap_noc',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error('Erreur de chargement');
      const result = await res.json();
      setData(result);
    } catch (err) {
      console.error('Erreur fetch:', err);
      message.error('Impossible de charger les donnees.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiUrl]);

  const handlePatch = async (id, field, value) => {
    try {
      const res = await fetch(`${API_URL}/validations/pb_cnx/${id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error('Erreur PATCH');
      setData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
      );
      message.success('Mise a jour reussie');
      setEditingField({ id: null, field: null });
    } catch {
      message.error('Erreur lors de la mise a jour');
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/validations/pb_cnx/${id}/`, { method: 'DELETE' });
      setData((prev) => prev.filter((item) => item.id !== id));
      message.success('Ligne supprimee');
    } catch {
      message.error('Suppression echouee');
    }
  };

  const handleAddRow = async () => {
    if (!newRow.email || !newRow.poste || !newRow.applications) {
      message.warning('Veuillez remplir les champs obligatoires.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/validations/pb_cnx/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(newRow),
      });
      if (!res.ok) throw new Error();
      await fetchData();
      setDrawerVisible(false);
      message.success('Nouvelle ligne ajoutee');
      setNewRow({
        email: '',
        poste: '',
        applications: '',
        comportement: '',
        prise_en_compte: false,
        traitee_verifiee: false,
        commentaire: '',
        ot_tag: 'cap_noc',
      });
    } catch {
      message.error('Impossible d’ajouter la ligne.');
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'email' ? (
          <Space>
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 200 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'email', editValue)} />
          </Space>
        ) : (
          <Space>
            <div>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'email' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Poste',
      dataIndex: 'poste',
      key: 'poste',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'poste' ? (
          <Space>
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 150 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'poste', editValue)} />
          </Space>
        ) : (
          <Space>
            <div>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'poste' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Applications',
      dataIndex: 'applications',
      key: 'applications',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'applications' ? (
          <Space>
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 150 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'applications', editValue)} />
          </Space>
        ) : (
          <Space>
            <div>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'applications' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: "Comportement observe (Description des manipulations, Message d'erreur, etc)",
      dataIndex: 'comportement',
      key: 'comportement',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'comportement' ? (
          <Space>
            <Input.TextArea rows={2} value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 250 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'comportement', editValue)} />
          </Space>
        ) : (
          <Space>
            <div style={{ whiteSpace: 'pre-wrap', maxWidth: 250 }}>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'comportement' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Prise en compte',
      dataIndex: 'prise_en_compte',
      key: 'prise_en_compte',
      render: (value, record) => (
        <Tag color={value ? 'green' : 'red'} icon={value ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#d93025" />}>
          {value ? 'Oui' : 'Non'}
        </Tag>
      ),
    },
    {
      title: 'Traitee / Verifiee',
      dataIndex: 'traitee_verifiee',
      key: 'traitee_verifiee',
      render: (value, record) => (
        <Tag color={value ? 'blue' : 'orange'} icon={value ? <CheckCircleTwoTone twoToneColor="#1890ff" /> : <CloseCircleTwoTone twoToneColor="#fa8c16" />}>
          {value ? 'Traitee' : 'A traiter'}
        </Tag>
      ),
    },
    {
      title: 'Commentaire',
      dataIndex: 'commentaire',
      key: 'commentaire',
      render: (text, record) =>
        editingField.id === record.id && editingField.field === 'commentaire' ? (
          <Space>
            <Input.TextArea rows={2} value={editValue} onChange={(e) => setEditValue(e.target.value)} style={{ width: 220 }} />
            <Button type="primary" icon={<SaveOutlined />} onClick={() => handlePatch(record.id, 'commentaire', editValue)} />
          </Space>
        ) : (
          <Space>
            <div style={{ whiteSpace: 'pre-wrap', maxWidth: 220 }}>{text}</div>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                setEditingField({ id: record.id, field: 'commentaire' }) || setEditValue(text || '')
              }
            />
          </Space>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)}>
          Supprimer
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, background: '#f5f7fb', minHeight: '100vh' }}>
      <NavigationStepsCapNoc selectedKey="incidents_habilitations" />

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Declaration de problemes de connexion — Cap Noc
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Rafraîchir
            </Button>
            <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => setDrawerVisible(true)}>
              Ajouter
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          bordered
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Drawer
        title="Ajouter un incident"
        width={420}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Email">
            <Input value={newRow.email} onChange={(e) => setNewRow({ ...newRow, email: e.target.value })} />
          </Form.Item>
          <Form.Item label="Poste">
            <Input value={newRow.poste} onChange={(e) => setNewRow({ ...newRow, poste: e.target.value })} />
          </Form.Item>
          <Form.Item label="Applications">
            <Input value={newRow.applications} onChange={(e) => setNewRow({ ...newRow, applications: e.target.value })} />
          </Form.Item>
          <Form.Item label="Comportement">
            <Input.TextArea rows={4} value={newRow.comportement} onChange={(e) => setNewRow({ ...newRow, comportement: e.target.value })} />
          </Form.Item>
          <Form.Item label="Commentaire">
            <Input.TextArea rows={3} value={newRow.commentaire} onChange={(e) => setNewRow({ ...newRow, commentaire: e.target.value })} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" block onClick={handleAddRow}>
              Enregistrer
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
